/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `useSideMenu` for the Caintra Connect frontend.
 * @module components/SideMenu/useSideMenu
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import { getUser } from "../../logic/user";
import { getCompanyByRFCForUser } from "../../logic/company";
import { signOut } from "../../utils/common.js";
import { useAuthSession } from "../../context/AuthSessionContext";
import { BREAKPOINTS, CONTEXTS, ROUTES } from "./constants";
import {
  isCustomizeEventRoute,
  isMobileViewport,
  isTabletViewport,
  setSideMenuWidthVar,
} from "./sideMenuUtils";
import styles from "../../styles/SideMenu.module.css";

/**
 * Encapsulates SideMenu state, effects, and action handlers.
 * Keeps the presentation components free of business logic.
 */
const useSideMenu = ({ onMobileNavigation }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions, clearSession } = useAuthSession();

  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [company, setCompany] = useState(null);
  const [isTablet, setIsTablet] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [personalizarEventoExpanded, setPersonalizarEventoExpanded] =
    useState(false);

  // Sync collapse state and CSS variable whenever the viewport changes.
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const userPreference = localStorage.getItem("menuCollapsed");

      setIsTablet(isTabletViewport(width));

      if (isMobileViewport(width)) {
        setIsCollapsed(true);
        setSideMenuWidthVar(true);
        return;
      }

      const collapsedPref = userPreference === "true";
      setIsCollapsed(collapsedPref);
      setSideMenuWidthVar(collapsedPref);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Expand "Personalizar evento" when navigating within its route group.
  useEffect(() => {
    if (isCustomizeEventRoute(location.pathname)) {
      setPersonalizarEventoExpanded(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchCompanyData = async () => {
      const companyRFC = user?.RFC;
      const userId = localStorage.getItem("userId");
      if (!companyRFC || !userId) {
        return;
      }
      const companyData = await getCompanyByRFCForUser(
        userId,
        companyRFC,
        navigate,
      );
      if (companyData) {
        setCompany(companyData);
      }
    };

    fetchCompanyData();
  }, [user?.RFC, user?.tipoDeUsuario, navigate]);

  // Close the profile dropdown when clicking outside its container.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showUserDropdown &&
        !event.target.closest(`.${styles.userProfileDropdown}`)
      ) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserDropdown]);

  const onSignOut = async () => {
    await signOut();
    clearSession();
    navigate("/");
  };

  const toggleUserDropdown = (event) => {
    event.stopPropagation();
    setShowUserDropdown((prev) => !prev);
  };

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);

    if (window.innerWidth >= BREAKPOINTS.TABLET_MIN) {
      localStorage.setItem("menuCollapsed", newCollapsedState.toString());
    }

    if (isOpen && newCollapsedState) {
      setIsOpen(false);
    }

    setSideMenuWidthVar(newCollapsedState);
  };

  const navigateTo = useCallback(
    (path, options) => {
      navigate(path, options);
      if (onMobileNavigation) {
        onMobileNavigation();
      }
    },
    [navigate, onMobileNavigation],
  );

  const closeDropdownAndNavigate = useCallback(
    (path, options) => {
      setShowUserDropdown(false);
      navigateTo(path, options);
    },
    [navigateTo],
  );

  const handleMyCompanyClick = async () => {
    if (!company) {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        return;
      }

      const userData = await getUser(userId, navigate);
      if (userData?.RFC) {
        const companyData = await getCompanyByRFCForUser(
          userId,
          userData.RFC,
          navigate,
        );
        if (companyData) {
          setCompany(companyData);
          closeDropdownAndNavigate(ROUTES.COMPANY_PROFILE, {
            state: { company: companyData, context: CONTEXTS.MY_COMPANY },
          });
          return;
        }
        closeDropdownAndNavigate(ROUTES.DIRECTORY, {
          state: { context: CONTEXTS.DIRECTORY },
        });
        return;
      }
    }

    closeDropdownAndNavigate(ROUTES.COMPANY_PROFILE, {
      state: { company, context: CONTEXTS.MY_COMPANY },
    });
  };

  return {
    user,
    permissions,
    isCollapsed,
    isTablet,
    showUserDropdown,
    personalizarEventoExpanded,
    setPersonalizarEventoExpanded,
    toggleUserDropdown,
    toggleCollapse,
    onSignOut,
    navigateTo,
    closeDropdownAndNavigate,
    handleMyCompanyClick,
    setShowUserDropdown,
  };
};

export default useSideMenu;
